import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon } from '@/components/icons';
import {
  C1Chip,
  C1Dashboard,
  C1Panel,
  C1StatusList,
  C1Tile,
  type C1StatusRow,
} from '@/components/c1';
import { formatDate, formatMoney } from '@/utils/format';
import { useEduCoreLaunchUrl } from '@/hooks/useEduCoreLaunchUrl';
import { useInstitutionTerms, type InstitutionVocabParams } from '@/hooks/useInstitutionTerms';
import { useOptionalAppContext } from '@/context/AppContextProvider';
import { EduCorePaths, type EduCorePath } from '@/lib/educoreLinks';
import { OVERVIEW_ATTENTION_PATH } from '@/lib/educorePanelLinks';
import type { AcademicOverviewInsights, OverviewAvailability, OverviewDomain } from '../types';
import { humanizeKind, sortAttention } from './shared';
import {
  AcademicsSection,
  AdmissionsFunnelSection,
  BoardingSection,
  FinanceSection,
  PathwaysSection,
  StaffingSection,
  UnavailableSection,
} from './sections';

/**
 * The executive command centre a head of institution reads before nine o'clock.
 *
 * COMPOSITION OVER COLLECTION. The old panel was a flat run of tiles; this one is
 * ordered by the questions a director actually asks in sequence — who is here
 * today, what needs me, how are the units doing, where is the money — with the
 * rail carrying the two things that change a morning (today's register and the
 * attention list) and the main column carrying the things read weekly.
 *
 * EVERY REGION IS GUARDED INDIVIDUALLY. The executive contract is additive and
 * optional; a payload that carries only part of it renders only that part, and
 * absence, no-activity, not-configured and unavailable each keep their own
 * rendering. Backend absence is NEVER drawn as zero.
 */
export function ExecutiveOverviewPanel({
  data,
  locale,
  institutionId,
}: {
  data: AcademicOverviewInsights;
  locale: string;
  institutionId?: string;
}) {
  const { t } = useTranslation();
  const educore = useEduCoreLaunchUrl(institutionId);
  const { vocab } = useInstitutionTerms(institutionId);
  const appContext = useOptionalAppContext();
  const open = (path: EduCorePath) => educore.href(path) ?? undefined;

  const fees = data.fees && data.fees.entitled ? data.fees : null;
  const currency = data.finance?.currency ?? fees?.currency ?? null;
  const money = (value: number) => formatMoney(value, currency, locale);
  const number = (value: number) => new Intl.NumberFormat(locale).format(value);

  const caps = data.capabilities ?? {};
  const availability = (domain: OverviewDomain): OverviewAvailability | undefined =>
    data.availability?.[domain];

  const today = data.attendance.today;
  const boarding = caps.boarding === false ? null : (data.boarding ?? null);
  const funnel = caps.admissions === false ? null : (data.admissionsFunnel ?? null);
  const showFinance = caps.finance !== false && Boolean(data.finance);

  /* ── A. Executive header strip — one quiet line of scope, never a banner. ── */
  const institutionName = appContext?.activeContext?.institution?.name;
  const metaParts = [
    institutionName,
    data.academicYear?.label,
    data.period?.label,
    data.asOf ? t('insights.overview.exec.asOf', { date: formatDate(data.asOf, locale) }) : null,
  ].filter((part): part is string => Boolean(part));

  /* ── B. KPI strip — five figures, each stating its period and calculation. ── */
  const tiles = buildKpiTiles({ data, t, vocab, number, money, open, today, showFinance });

  /* ── Rail: TODAY, ATTENTION, APPROVALS — compact, in reading order. ── */
  const aside = (
    <>
      <TodayPanel data={data} boardingCheckedIn={boarding?.checkedIn} number={number} open={open} />
      <AttentionRail data={data} vocab={vocab} number={number} open={open} />
      <ApprovalsPanel data={data} boardingCapability={caps.boarding !== false} number={number} />
      <CountsLine data={data} vocab={vocab} number={number} />
    </>
  );

  return (
    <C1Dashboard aside={aside} asideLabel={t('insights.overview.exec.railLabel')} stickyAside>
      {metaParts.length > 0 && (
        <p className="insight-scopeNote insight-execMeta">{metaParts.join(' · ')}</p>
      )}
      {data.institutionWide && (
        <p className="insight-scopeNote">
          {t('insights.overview.institutionWide')}
          {educore.launchUrl
            ? ` · ${t('insights.clickToOpen')}`
            : ` · ${t('insights.educoreNotLinked')}`}
        </p>
      )}

      <div className="insight-tiles">{tiles}</div>

      {/* E. The academic pathways table — the rows a head compares. */}
      <PathwaysSection data={data} vocab={vocab} locale={locale} boardingCapability={caps.boarding !== false} />

      {/* F. Attendance & academics. */}
      {availability('academics') === 'UNAVAILABLE' || availability('attendance') === 'UNAVAILABLE' ? (
        <UnavailableSection title={t('insights.overview.exec.academicsTitle')} />
      ) : (
        <AcademicsSection data={data} vocab={vocab} locale={locale} />
      )}

      {/* G. Financial position — main content, not a rail card. */}
      {availability('finance') === 'UNAVAILABLE' ? (
        <UnavailableSection title={t('insights.overview.exec.financeTitle')} />
      ) : availability('finance') === 'NOT_CONFIGURED' ? null : showFinance ? (
        <FinanceSection finance={data.finance!} money={money} number={number} open={open} />
      ) : null}

      {/* H. Admissions funnel — capability-gated; null means the school does not run one. */}
      {availability('admissions') === 'UNAVAILABLE' ? (
        <UnavailableSection title={t('insights.overview.exec.admissionsTitle')} />
      ) : funnel ? (
        <AdmissionsFunnelSection funnel={funnel} number={number} open={open} />
      ) : null}

      {/* I. Boarding — capability-gated the same way. */}
      {availability('boarding') === 'UNAVAILABLE' ? (
        <UnavailableSection title={t('insights.overview.exec.boardingTitle')} />
      ) : boarding ? (
        <BoardingSection boarding={boarding} number={number} />
      ) : null}

      {/* J. Staffing. */}
      {data.staffing ? <StaffingSection staffing={data.staffing} vocab={vocab} number={number} /> : null}
    </C1Dashboard>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */

type Translate = (key: string, options?: Record<string, unknown>) => string;

function buildKpiTiles({
  data,
  t,
  vocab,
  number,
  money,
  open,
  today,
  showFinance,
}: {
  data: AcademicOverviewInsights;
  t: Translate;
  vocab: InstitutionVocabParams;
  number: (value: number) => string;
  money: (value: number) => { display: string; exact: string };
  open: (path: EduCorePath) => string | undefined;
  today: AcademicOverviewInsights['attendance']['today'];
  showFinance: boolean;
}) {
  const tiles = [];

  // Enrolled learners. Falls back to the legacy roll count when population is absent.
  const enrolled = data.population?.enrolled ?? data.students;
  tiles.push(
    <C1Tile
      key="enrolled"
      label={
        <span title={t('insights.overview.exec.enrolledHint', vocab)}>
          {t('insights.overview.exec.enrolled', vocab)}
        </span>
      }
      value={number(enrolled)}
      sub={
        data.population
          ? t('insights.overview.exec.activeCount', { count: data.population.active })
          : undefined
      }
      icon={<Icon name="users" size={16} />}
      href={open(EduCorePaths.students)}
    />,
  );

  // Staff. staffing (new) beats staff (legacy); absence renders nothing, never zero.
  if (data.staffing) {
    tiles.push(
      <C1Tile
        key="staff"
        label={
          <span title={t('insights.overview.exec.staffHint')}>
            {t('insights.overview.exec.staff', vocab)}
          </span>
        }
        value={number(data.staffing.total)}
        sub={t('insights.overview.exec.teachingCount', { count: data.staffing.teaching })}
        icon={<Icon name="teacher" size={16} />}
        href={open(EduCorePaths.directorOverview)}
      />,
    );
  } else if (data.staff) {
    tiles.push(
      <C1Tile
        key="staff"
        label={t('insights.overview.staff', vocab)}
        value={number(data.staff.total)}
        sub={t('insights.overview.staffActive', { count: data.staff.active })}
        icon={<Icon name="teacher" size={16} />}
        href={open(EduCorePaths.directorOverview)}
      />,
    );
  }

  // Attendance today. NO_ACTIVITY is a sentence, never 0%.
  if (today) {
    const noActivity = today.state === 'NO_ACTIVITY' || today.rate === null;
    tiles.push(
      <C1Tile
        key="attendanceToday"
        label={
          <span title={t('insights.overview.exec.attendanceTodayHint', vocab)}>
            {t('insights.overview.attendanceToday')}
          </span>
        }
        value={noActivity ? '—' : `${today.rate}%`}
        sub={
          noActivity
            ? t('insights.overview.exec.noRegisterRecorded')
            : t('insights.overview.exec.absentCount', { count: today.absent })
        }
        icon={<Icon name="pulse" size={16} />}
        href={open(EduCorePaths.attendance)}
        sparkline={noActivity ? undefined : data.attendance.series.map((entry) => entry.rate)}
      />,
    );
  } else if (data.attendanceToday) {
    const todayRate = data.attendanceToday.rate;
    const againstAverage =
      todayRate !== null && data.attendance.rate !== null
        ? Math.round((todayRate - data.attendance.rate) * 10) / 10
        : null;
    tiles.push(
      <C1Tile
        key="attendanceToday"
        label={t('insights.overview.attendanceToday')}
        value={todayRate === null ? '—' : `${todayRate}%`}
        sub={todayRate === null ? t('insights.overview.noRegisterYet') : undefined}
        icon={<Icon name="pulse" size={16} />}
        href={open(EduCorePaths.attendance)}
        trend={{
          delta: againstAverage,
          unit: '%',
          comparison: t('insights.overview.vsAverage'),
          goodWhen: 'up',
        }}
        sparkline={data.attendance.series.map((entry) => entry.rate)}
      />,
    );
  } else {
    tiles.push(
      <C1Tile
        key="attendance"
        label={t('insights.overview.attendance')}
        value={data.attendance.rate === null ? '—' : `${data.attendance.rate}%`}
        icon={<Icon name="pulse" size={16} />}
        href={open(EduCorePaths.attendance)}
      />,
    );
  }

  // Academic average, with the period it covers stated beside it.
  const average = data.academics?.average ?? data.grades.average;
  const assessed = data.academics?.assessed ?? data.grades.assessed;
  const averageSub = [
    t('insights.overview.exec.assessedCount', { count: assessed }),
    data.period?.label,
  ]
    .filter(Boolean)
    .join(' · ');
  tiles.push(
    <C1Tile
      key="average"
      label={
        <span title={t('insights.overview.exec.averageHint')}>
          {t('insights.overview.exec.average')}
        </span>
      }
      value={average === null ? '—' : `${average}%`}
      sub={averageSub}
      icon={<Icon name="academic" size={16} />}
      href={open(EduCorePaths.grades)}
    />,
  );

  // Fee collection rate, explicitly scoped to the academic year. Nothing billed
  // is a sentence, not a nought per cent.
  // `year` is guarded rather than assumed: a payload whose finance block arrives in
  // an unexpected shape must cost the school one tile, never the whole page.
  if (showFinance && data.finance?.year) {
    const year = data.finance.year;
    tiles.push(
      <C1Tile
        key="collection"
        label={
          <span title={t('insights.overview.exec.collectionRateHint')}>
            {t('insights.overview.exec.collectionRate')}
          </span>
        }
        value={
          year.collectionRate === null
            ? t('insights.overview.nothingBilled')
            : `${year.collectionRate}%`
        }
        sub={`${t('insights.overview.exec.academicYearScope')} · ${t(
          'insights.overview.exec.outstandingShort',
          { amount: money(year.outstanding).display },
        )}`}
        icon={<Icon name="report" size={16} />}
        href={open(EduCorePaths.reports)}
        tone={year.collectionRate !== null && year.collectionRate < 50 ? 'warning' : 'default'}
      />,
    );
  } else if (fees(data)) {
    const ledger = fees(data)!;
    tiles.push(
      <C1Tile
        key="collectedThisMonth"
        label={t('insights.overview.collectedThisMonth')}
        value={money(ledger.collectedThisMonth).display}
        sub={t('insights.overview.paymentsCount', { count: ledger.paymentsThisMonth })}
        icon={<Icon name="report" size={16} />}
        href={open(EduCorePaths.fees)}
      />,
    );
  }

  return tiles;
}

function fees(data: AcademicOverviewInsights) {
  return data.fees && data.fees.entitled ? data.fees : null;
}

/* ── C. TODAY — the register as it stands, only the dimensions that arrived. ── */
function TodayPanel({
  data,
  boardingCheckedIn,
  number,
  open,
}: {
  data: AcademicOverviewInsights;
  boardingCheckedIn: number | undefined;
  number: (value: number) => string;
  open: (path: EduCorePath) => string | undefined;
}) {
  const { t } = useTranslation();
  const today = data.attendance.today;
  if (!today && boardingCheckedIn === undefined) return null;

  if (today && today.state === 'NO_ACTIVITY') {
    return (
      <C1Panel title={t('insights.overview.exec.todayTitle')}>
        {/* Honest system status: absence of a register, stated as such — a screen
            of zeroes would report total truancy nobody measured. */}
        <p className="insight-scopeNote">{t('insights.overview.exec.noRegisterRecorded')}</p>
      </C1Panel>
    );
  }

  const rows: C1StatusRow[] = [];
  if (today) {
    const dimension = (
      id: string,
      value: number | undefined,
      tone?: C1StatusRow['tone'],
    ) => {
      if (typeof value !== 'number') return;
      rows.push({
        id,
        label: t(`insights.overview.exec.${id}`),
        value: number(value),
        tone,
        href: open(EduCorePaths.attendance),
      });
    };
    dimension('recorded', today.recorded);
    dimension('present', today.present);
    dimension('absent', today.absent, today.absent > 0 ? 'warning' : undefined);
    dimension('late', today.late);
    dimension('excused', today.excused);
  }
  if (typeof boardingCheckedIn === 'number') {
    rows.push({
      id: 'boardersCheckedIn',
      label: t('insights.overview.exec.boardersCheckedIn'),
      value: number(boardingCheckedIn),
    });
  }
  if (rows.length === 0) return null;

  return (
    <C1Panel title={t('insights.overview.exec.todayTitle')}>
      <C1StatusList items={rows} />
    </C1Panel>
  );
}

/* ── D. REQUIRES YOUR ATTENTION — the flagship, sorted HIGH first. ── */
function AttentionRail({
  data,
  vocab,
  number,
  open,
}: {
  data: AcademicOverviewInsights;
  vocab: InstitutionVocabParams;
  number: (value: number) => string;
  open: (path: EduCorePath) => string | undefined;
}) {
  const { t } = useTranslation();

  if (data.attention === undefined) {
    // Old server: keep the at-risk count card that used to fill this slot.
    if (!data.atRisk) return null;
    return (
      <C1Panel title={t('insights.overview.attentionTitle')}>
        {data.atRisk.count > 0 ? (
          <C1Tile
            label={t('insights.overview.atRisk', { threshold: data.atRisk.threshold, ...vocab })}
            value={number(data.atRisk.count)}
            tone="warning"
            icon={<Icon name="warning" size={16} />}
            href={open(EduCorePaths.attendance)}
          />
        ) : (
          <p className="insight-scopeNote">{t('insights.overview.exec.attentionNone')}</p>
        )}
      </C1Panel>
    );
  }

  if (data.attention.length === 0) {
    // A single quiet line, not a giant celebratory card. The absence of trouble is
    // the normal state and should read like one.
    return (
      <C1Panel title={t('insights.overview.attentionTitle')}>
        <p className="insight-scopeNote">{t('insights.overview.exec.attentionNone')}</p>
      </C1Panel>
    );
  }

  const chipTone = { HIGH: 'danger', MEDIUM: 'warning', INFO: 'neutral' } as const;
  const rowTone = { HIGH: 'critical', MEDIUM: 'warning', INFO: 'neutral' } as const;

  const rows: C1StatusRow[] = sortAttention(data.attention).map((item, index) => {
    const path = OVERVIEW_ATTENTION_PATH[item.kind];
    return {
      id: `${item.kind}-${index}`,
      tone: rowTone[item.severity] ?? 'neutral',
      // Severity is words on a chip as well as a tone — never colour alone.
      label: (
        <>
          <C1Chip tone={chipTone[item.severity] ?? 'neutral'}>
            {t(`insights.overview.exec.severity.${item.severity}`)}
          </C1Chip>{' '}
          {t(`insights.overview.exec.attention.${item.kind}`, {
            count: item.count,
            defaultValue: humanizeKind(item.kind),
            ...vocab,
          })}
        </>
      ),
      value: number(item.count),
      href: path ? open(path) : undefined,
    };
  });

  return (
    <C1Panel title={t('insights.overview.attentionTitle')}>
      <C1StatusList items={rows} />
    </C1Panel>
  );
}

/* ── K. APPROVALS — only when the payload carries them; no invented queue. ── */
function ApprovalsPanel({
  data,
  boardingCapability,
  number,
}: {
  data: AcademicOverviewInsights;
  boardingCapability: boolean;
  number: (value: number) => string;
}) {
  const { t } = useTranslation();
  const approvals = data.approvals;
  if (!approvals) return null;

  const rows: C1StatusRow[] = [];
  if (approvals.markCorrections > 0) {
    rows.push({
      id: 'markCorrections',
      label: t('insights.overview.exec.markCorrections'),
      value: number(approvals.markCorrections),
      tone: 'warning',
    });
  }
  if (approvals.boardingLeave > 0 && boardingCapability) {
    rows.push({
      id: 'boardingLeave',
      label: t('insights.overview.exec.boardingLeave'),
      value: number(approvals.boardingLeave),
      tone: 'warning',
    });
  }

  return (
    <C1Panel
      title={t('insights.overview.exec.approvalsTitle')}
      action={
        <Link to="/approvals" className="c1-tableToggle">
          {t('insights.overview.exec.approvalsReview')}
        </Link>
      }
    >
      {approvals.total === 0 ? (
        <p className="insight-scopeNote">{t('insights.overview.exec.approvalsNone')}</p>
      ) : (
        <C1StatusList items={rows} />
      )}
    </C1Panel>
  );
}

/**
 * The modest counts line that replaced the old counter list: programme and class
 * totals in one sentence, and no "Departments: 0" row — an absent or zero unit
 * count says the school does not structure itself that way, not that it has none.
 */
function CountsLine({
  data,
  vocab,
  number,
}: {
  data: AcademicOverviewInsights;
  vocab: InstitutionVocabParams;
  number: (value: number) => string;
}) {
  const { t } = useTranslation();
  const parts: string[] = [];
  if (typeof data.programmes === 'number' && data.programmes > 0) {
    parts.push(
      t('insights.overview.exec.programmesCount', { count: number(data.programmes), ...vocab }),
    );
  }
  if (data.classes && data.classes.active > 0) {
    parts.push(
      t('insights.overview.exec.classesCount', { count: number(data.classes.active), ...vocab }),
    );
  }
  if (typeof data.departments === 'number' && data.departments > 0) {
    parts.push(
      t('insights.overview.exec.departmentsCount', { count: number(data.departments), ...vocab }),
    );
  }
  if (parts.length === 0) return null;
  return <p className="insight-scopeNote insight-countsLine">{parts.join(' · ')}</p>;
}
